import { type ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import { DateTime } from "luxon";
import type { Event } from "../../../../api/useEvents";
import type { ExternalGaps } from "../../../../api/useExternalGaps";
import { type IndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import { type RawFailureValues } from "../../../../api/useIndexedFailureValues";
import { type useIndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import { type ManualFailure } from "../../../../api/useManualFailures";
import { type MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import { type ProjectCategory } from "../../../../api/useProjectCategories";
import { type Project } from "../../../../api/useProjects";
import { type Quantity, type QuantityIndex } from "../../../../api/useQuantities";
import { type Setup } from "../../../../api/useSetups";
import { getEventEndsAt, getEventPriority, isMultiFaultEvent } from "../../../../domain/event";
import { isFailureValueInFaultEventOccurrencePeriods } from "diagnostics-domain/faultEvent";
import { inv } from "../../../../lib/chiSquared";
import { computeQuantityGap, computeQuantityNormalizedGap } from "diagnostics-domain/quantity/gap";
import type { MetrologyStatus } from "diagnostics-domain/quantity/metrology";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { type Group } from "../../../../api/useGroups";
import { getAnalysisQuantityMetrologyStatus } from "../../metrology";
import { type ActiveAnalysisColumnsT } from "../schema/resource/analysis";
import { ActiveAnalysisColumns } from "../schema/resource/analysis";
import {
  FaultComputedValueColumns,
  FaultConstantColumns,
  FaultEventColumns,
  FaultValueColumns,
  QuantityAnalysisSetupComputedColumns,
  QuantityAnalysisSetupInputColumns,
  QuantityAnalysisSetupOutputColumns,
  QuantityAnalysisValueColumns,
  QuantityConstantColumns,
  type FaultConstantColumnsT,
  type FaultValueColumnsT,
  type QuantityAnalysisSetupInputColumnsT,
  type QuantityAnalysisSetupOutputColumnsT,
  type QuantityAnalysisValueColumnsT,
  type QuantityConstantColumnsT,
} from "../schema/resource/columns";
import { type FaultResourceT } from "../schema/resource/fault";
import { type QuantityResourceT } from "../schema/resource/quantity";

function getMetrologyStatus(
  quantity: { id: string },
  analysis: ActiveAnalysis,
  _metrologyOverrides: { [date: number]: { [quantityName: string]: boolean } },
  quantityIndex: QuantityIndex,
): MetrologyStatus {
  const quantityMetrology = quantityIndex[quantity.id];
  if (!quantityMetrology) return "NO_VALIDATION";

  return getAnalysisQuantityMetrologyStatus(analysis, quantityMetrology);
}

// TODO TEMPORAL AGGREGATION

/**
 * @description
 * Pick value from available data related to quantities
 * from our API Endpoint results.
 *
 * Fails silently if the column don't match any key in our data.
 *
 * @note
 * If any of the value is an array, we return an array of tuples
 * that can be transformed with Object.fromEntries.
 *
 * @see {mapResourceToDataset}
 */
export function mapQuantityResourceToValue({
  quantities,
  equipments,
  metrology,
  groups,
  setups,
  metrologyOverrides,
  externalGaps,
  timezone,
}: {
  quantities: Quantity[];
  equipments: ProjectCategory[];
  metrology: QuantityIndex;
  groups?: Group[];
  setups?: Setup[];
  metrologyOverrides?: MetrologyOverrides;
  externalGaps?: ExternalGaps;
  timezone: string;
}) {
  const quantitiesByName = new Map(quantities.map(quantity => [quantity.name, quantity] as const));
  const equipmentsById = new Map(equipments.map(equipment => [equipment.id, equipment] as const));
  const groupByDateCache = new Map<number, Group | null>();
  const analysisSetupCache = new Map<string | number, Setup | null>();
  const diagnosisInputsBySetup = new Map<
    Setup["id"],
    Map<string, Setup["diagnosisInputs"][number]>
  >(
    setups?.map(setup => [
      setup.id,
      new Map(setup.diagnosisInputs.map(input => [input.quantityId, input])),
    ]) ?? [],
  );
  const simulationOutputsBySetup = new Map<
    Setup["id"],
    Map<string, Setup["simulationOutputs"][number]>
  >(
    setups?.map(setup => [
      setup.id,
      new Map(setup.simulationOutputs.map(output => [output.quantityId, output])),
    ]) ?? [],
  );

  const getDiagnosisInputsBySetup = (setup: Setup) => {
    const cached = diagnosisInputsBySetup.get(setup.id);
    if (cached) return cached;
    const map = new Map(setup.diagnosisInputs.map(input => [input.quantityId, input] as const));
    diagnosisInputsBySetup.set(setup.id, map);
    return map;
  };

  const getSimulationOutputsBySetup = (setup: Setup) => {
    const cached = simulationOutputsBySetup.get(setup.id);
    if (cached) return cached;
    const map = new Map(
      setup.simulationOutputs.map(output => [output.quantityId, output] as const),
    );
    simulationOutputsBySetup.set(setup.id, map);
    return map;
  };

  const getGroupForDate = (date: number) => {
    if (!groups) return null;
    if (groupByDateCache.has(date)) return groupByDateCache.get(date) ?? null;
    const group =
      groups.find(({ startDate, endDate }) => startDate <= date && date <= endDate) ?? null;
    groupByDateCache.set(date, group);
    return group;
  };

  const getSetupForAnalysis = (analysis: ActiveAnalysis) => {
    if (!setups) return null;
    const key = analysis.id ?? analysis.date;
    if (analysisSetupCache.has(key)) return analysisSetupCache.get(key) ?? null;
    const setup = getAnalysisSetup(setups, analysis);
    analysisSetupCache.set(key, setup ?? null);
    return setup ?? null;
  };

  return function (analysis?: ActiveAnalysis) {
    return function (identifier: string, column: string, quantity?: string) {
      const projectQuantity = quantitiesByName.get(identifier);

      if (!projectQuantity) return null;

      if (analysis && column === "dayOfYear") {
        const dateTime = DateTime.fromMillis(analysis.date).setZone(timezone);
        return dateTime.ordinal;
      }

      if (analysis && column === "hour") {
        const dateTime = DateTime.fromMillis(analysis.date).setZone(timezone);
        return dateTime.hour;
      }

      const isQuantityConstant = QuantityConstantColumns.decode(column)._tag === "Right";
      const isActiveAnalysisColumn =
        analysis && ActiveAnalysisColumns.decode(column)._tag === "Right";
      const isQuantityAnalysisValue =
        analysis && QuantityAnalysisValueColumns.decode(column)._tag === "Right";
      const isQuantitySetupInput =
        analysis && setups && QuantityAnalysisSetupInputColumns.decode(column)._tag === "Right";
      const isQuantitySetupOutput =
        analysis && setups && QuantityAnalysisSetupOutputColumns.decode(column)._tag === "Right";
      const isQuantitySetupComputed =
        analysis && setups && QuantityAnalysisSetupComputedColumns.decode(column)._tag === "Right";

      if (isQuantityConstant) {
        if (column === "sensor") return projectQuantity[column as "sensor"]?.tag;

        if (column === "equipmentIds")
          return projectQuantity[column as "equipmentIds"]?.map((equipment, i) => [
            `${column}.${i}`,
            equipmentsById.get(equipment)?.name,
          ]);

        return projectQuantity[
          column as Exclude<
            QuantityConstantColumnsT,
            "sensor" | "equipmentIds" | "expected" | "gap" | "normalizedGap"
          >
        ];
      }

      if (isActiveAnalysisColumn) {
        if (column === "group") {
          const group = getGroupForDate(analysis.date);
          return group ? `${group.title}-${group.startDate}-${group.endDate}` : null;
        } else {
          return analysis[column as ActiveAnalysisColumnsT];
        }
      }

      if (isQuantityAnalysisValue)
        return analysis.quantityValues[projectQuantity.id]?.[
          column as QuantityAnalysisValueColumnsT
        ];

      if (isQuantitySetupInput) {
        const setup = getSetupForAnalysis(analysis);
        const setupInputs = setup ? getDiagnosisInputsBySetup(setup) : null;
        return setupInputs?.get(projectQuantity.id)?.[column as QuantityAnalysisSetupInputColumnsT];
      }

      if (isQuantitySetupOutput) {
        const setup = getSetupForAnalysis(analysis);
        const setupOutputs = setup ? getSimulationOutputsBySetup(setup) : null;
        return setupOutputs?.get(projectQuantity.id)?.[
          column as QuantityAnalysisSetupOutputColumnsT
        ];
      }

      if (isQuantitySetupComputed) {
        if (column === "externalImpacts" && quantity) {
          const performanceIndicator = quantitiesByName.get(quantity);
          const value =
            externalGaps && performanceIndicator
              ? externalGaps[projectQuantity.id]?.[performanceIndicator.id]?.[analysis.id]
              : null;
          return [
            [`externalImpacts/${quantity}`, value],
            [`externalImpactsUnit/${quantity}`, performanceIndicator?.unit],
          ];
        }

        const setup = getSetupForAnalysis(analysis);
        const setupInputs = setup ? getDiagnosisInputsBySetup(setup) : null;
        const setupOutputs = setup ? getSimulationOutputsBySetup(setup) : null;
        const measured = analysis.quantityValues[projectQuantity.id]?.["measured"];
        const simulated = analysis.quantityValues[projectQuantity.id]?.["simulated"];
        const offset = setupOutputs?.get(projectQuantity.id)?.["offsetExpected"];
        const sigma = setupInputs?.get(projectQuantity.id)?.["sigma"];

        if (column === "expected" && typeof simulated === "number" && typeof offset === "number")
          return simulated + offset;

        if (!metrologyOverrides) return null;

        const validity = projectQuantity
          ? getMetrologyStatus(projectQuantity, analysis, metrologyOverrides, metrology)
          : "NO_VALIDATION";
        const canComputeFromValidity =
          validity === "VALID_AUTO" ||
          validity === "VALID_MANUAL" ||
          validity === "VALID_PARTIAL_INPUTS";

        if (
          canComputeFromValidity &&
          column === "gap" &&
          typeof measured === "number" &&
          typeof simulated === "number" &&
          typeof offset === "number"
        ) {
          return computeQuantityGap(measured, simulated, offset);
        }

        if (
          canComputeFromValidity &&
          column === "normalizedGap" &&
          typeof simulated === "number" &&
          typeof measured === "number" &&
          typeof offset === "number" &&
          typeof sigma === "number"
        ) {
          return computeQuantityNormalizedGap(measured, simulated, offset, sigma);
        }
      }

      if (
        metrologyOverrides &&
        analysis &&
        QuantityAnalysisSetupComputedColumns.decode(column)._tag === "Right" &&
        column === "validity"
      ) {
        return projectQuantity
          ? getMetrologyStatus(projectQuantity, analysis, metrologyOverrides, metrology)
          : "NO_VALIDATION";
      }

      return null;
    };
  };
}

/**
 * @description
 * Pick value from available data related to failures
 * from our API Endpoint results.
 *
 * Fails silently if the column don't match any key in our data.
 *
 * @note
 * If any of the value is an array, we return an array of tuples
 * that can be transformed with `Object.fromEntries`.
 *
 * @see mapResourceToDataset
 */
export function mapFaultResourceToValue({
  quantities,
  project,
  failureValueStatus,
  groups,
  setups,
  failureValues,
  manualFailureValues,
  manualFailures,
  eventsByFault,
  timezone,
}: {
  quantities: Quantity[];
  project: Project;
  failureValueStatus: IndexedFailureMetadata["failureValueStatus"];
  groups?: Group[];
  setups?: Setup[];
  failureValues?: RawFailureValues;
  manualFailureValues: ReturnType<typeof useIndexedManualFailureValues>;
  manualFailures: ManualFailure[];
  eventsByFault: Map<string, Event[]>;
  timezone: string;
}) {
  const quantitiesById = new Map(quantities.map(quantity => [quantity.id, quantity] as const));
  const quantitiesByName = new Map(quantities.map(quantity => [quantity.name, quantity] as const));
  const failuresByName = new Map<string, Setup["failures"][number]>();
  const manualFailuresByName = new Map(
    manualFailures.map(failure => [failure.name, failure] as const),
  );
  const groupByDateCache = new Map<number, Group | null>();
  const failureValuesByDateAndName = new Map<number, Map<string, RawFailureValues[number]>>();
  const failureStatusByDateAndName = new Map<
    number,
    Map<string, IndexedFailureMetadata["failureValueStatus"][number]>
  >();
  const diagnosisInputsBySetup = new Map<
    Setup["id"],
    Map<string, Setup["diagnosisInputs"][number]>
  >(
    setups?.map(setup => [
      setup.id,
      new Map(setup.diagnosisInputs.map(input => [input.quantityId, input])),
    ]) ?? [],
  );
  const impactsByFailure = new Map<
    Setup["failures"][number]["id"],
    Map<string, Setup["failures"][number]["impacts"][number][]>
  >();
  const observablesByFailure = new Map<
    Setup["failures"][number]["id"],
    Setup["diagnosisInputs"][number][]
  >();

  const getDiagnosisInputsBySetup = (setup: Setup) => {
    const cached = diagnosisInputsBySetup.get(setup.id);
    if (cached) return cached;
    const map = new Map(setup.diagnosisInputs.map(input => [input.quantityId, input] as const));
    diagnosisInputsBySetup.set(setup.id, map);
    return map;
  };

  const getImpactsByFailure = (failure: Setup["failures"][number]) => {
    const cached = impactsByFailure.get(failure.id);
    if (cached) return cached;
    const map = new Map<string, Setup["failures"][number]["impacts"][number][]>();
    for (const impact of failure.impacts ?? []) {
      const impacts = map.get(impact.quantityId) ?? [];
      impacts.push(impact);
      map.set(impact.quantityId, impacts);
    }
    impactsByFailure.set(failure.id, map);
    return map;
  };

  const getObservablesForFailure = (failure: Setup["failures"][number], setup: Setup) => {
    const cached = observablesByFailure.get(failure.id);
    if (cached) return cached;
    const setupInputs = getDiagnosisInputsBySetup(setup);
    const uniqueQuantityIds = new Set<string>();
    const observables: Setup["diagnosisInputs"][number][] = [];
    for (const impact of failure.impacts ?? []) {
      if (uniqueQuantityIds.has(impact.quantityId)) continue;
      uniqueQuantityIds.add(impact.quantityId);
      const observable = setupInputs.get(impact.quantityId);
      if (observable) observables.push(observable);
    }
    observablesByFailure.set(failure.id, observables);
    return observables;
  };

  if (failureValues) {
    for (const value of failureValues) {
      const byName = failureValuesByDateAndName.get(value.date) ?? new Map();
      byName.set(value.failureName, value);
      failureValuesByDateAndName.set(value.date, byName);
    }
  }

  for (const status of failureValueStatus) {
    if (status.projectId !== project.id) continue;
    const byName = failureStatusByDateAndName.get(status.date) ?? new Map();
    byName.set(status.failureName, status);
    failureStatusByDateAndName.set(status.date, byName);
  }

  const getGroupForDate = (date: number) => {
    if (!groups) return null;
    if (groupByDateCache.has(date)) return groupByDateCache.get(date) ?? null;
    const group =
      groups.find(({ startDate, endDate }) => startDate <= date && date <= endDate) ?? null;
    groupByDateCache.set(date, group);
    return group;
  };

  const extractEventData = (event: Event | undefined, identifier: string, column: string) => {
    if (!event) return;

    if (column === "eventName") {
      return event.name;
    } else if (column === "eventId") {
      return event.id;
    } else if (column === "eventHasMultiFault") {
      return isMultiFaultEvent(event);
    } else if (column === "eventType") {
      return event.type;
    } else if (column === "eventFaultName") {
      return identifier;
    } else if (column === "eventPriority") {
      return getEventPriority(event);
    } else if (column === "eventStartsAt") {
      return event.startsAt.getTime();
    } else if (column === "eventEndsAt") {
      return getEventEndsAt(event).getTime();
    } else return;
  };

  return function ({ setup, analysis }: { setup: Setup; analysis?: ActiveAnalysis }) {
    failuresByName.clear();
    for (const failure of setup["failures"]) {
      failuresByName.set(failure.name, failure);
    }

    return function (identifier: string, column: string, quantity?: string) {
      if (analysis && column === "dayOfYear") {
        const dateTime = DateTime.fromMillis(analysis.date).setZone(timezone);
        return dateTime.ordinal;
      }

      if (analysis && column === "hour") {
        const dateTime = DateTime.fromMillis(analysis.date).setZone(timezone);
        return dateTime.hour;
      }

      const isActiveAnalysisColumn =
        analysis && ActiveAnalysisColumns.decode(column)._tag === "Right";
      const isFaultConstant = FaultConstantColumns.decode(column)._tag === "Right";
      const isFaultValue = analysis && FaultValueColumns.decode(column)._tag === "Right";
      const isFaultComputed = analysis && FaultComputedValueColumns.decode(column)._tag === "Right";
      const isFaultEvent = analysis && FaultEventColumns.decode(column)._tag === "Right";

      if (isActiveAnalysisColumn) {
        if (column === "group") {
          const group = getGroupForDate(analysis.date);
          return group ? `${group.title}-${group.startDate}-${group.endDate}` : null;
        } else {
          return analysis[column as ActiveAnalysisColumnsT];
        }
      }

      const failure = failuresByName.get(identifier);
      const manualFailure = manualFailuresByName.get(identifier);

      if (failure) {
        if (isFaultConstant) {
          if (column === "systemId") {
            return failure.systemId === null ? "No system" : failure.systemId;
          }

          if (column === "filters") {
            return failure[column as "filters"]?.flatMap(filter => {
              const quantity = quantitiesById.get(filter.quantityId);
              return [
                [`${quantity?.name}.filterType`, filter.type],
                [`${quantity?.name}.filterMin`, filter.min],
                [`${quantity?.name}.filterMax`, filter.max],
              ];
            });
          }

          if (column === "impacts") {
            // @todo swap loop
            const impacts = failure[column as "impacts"]?.flatMap(impact => {
              const impactedQuantity = quantitiesById.get(impact.quantityId);
              return [
                [`impactValue/${impactedQuantity?.name}`, impact.value],
                [`impactUnit/${impactedQuantity?.name}`, impactedQuantity?.unit],
              ];
            });
            return impacts;
          }

          return failure[column as Exclude<FaultConstantColumnsT, "filters" | "impacts">];
        }

        if (failureValues && isFaultValue) {
          const value = failureValuesByDateAndName.get(analysis.date)?.get(failure.name)?.[
            column as Exclude<FaultValueColumnsT, "faultStatus">
          ];
          const reparability = failureStatusByDateAndName
            .get(analysis.date)
            ?.get(failure.name)?.reparability;
          return column === "reparability" ? (reparability ?? failure["reparability"]) : value;
        }

        if (failureValues && isFaultComputed) {
          const value = failureValuesByDateAndName.get(analysis.date)?.get(failure.name);
          const amplitude = failure["amplitude"];

          if (column === "faultStatus") {
            const status = failureStatusByDateAndName.get(analysis.date)?.get(failure.name)?.status;
            if (status === "REJECTED" || status === "LEGACY_REJECTED") return status;
            if (typeof value?.real === "number" && status) return status;
            if (typeof value?.real === "number" && !status) return "TO_BE_ANALYSED";
            return null;
          }

          if (column === "quantityImpacts") {
            const quantityId = quantitiesByName.get(quantity ?? "")?.id;
            if (!quantity || !quantityId) return [];
            else {
              const status = failureStatusByDateAndName
                .get(analysis.date)
                ?.get(failure.name)?.status;
              const impactsByQuantity = getImpactsByFailure(failure).get(quantityId) ?? [];
              return impactsByQuantity.map(impact => [
                `quantityImpacts/${quantity}`,
                value && typeof amplitude === "number" && status !== "LEGACY_REJECTED"
                  ? (value.real / amplitude) * impact.value
                  : null,
              ]);
            }
          }

          if (column === "globalImpact") {
            const setupInputs = getDiagnosisInputsBySetup(setup);
            const observables = getObservablesForFailure(failure, setup);

            const impact = (failure["impacts"] ?? []).reduce((sum, impact) => {
              const observable = setupInputs.get(impact.quantityId);
              const imp = value && amplitude ? (value.real / amplitude) * impact.value : null;
              return imp && observable ? sum + Math.pow(imp / observable.sigma, 2) : sum;
            }, 0);

            return Math.sqrt(impact / inv(0.95, observables.length)) * -1;
          }
        }

        if (isFaultEvent) {
          const relevantEvents = eventsByFault.get(identifier) ?? [];
          const eventHit = relevantEvents.find(
            e =>
              e.type === "DIAGNOSED" &&
              isFailureValueInFaultEventOccurrencePeriods(e, {
                projectId: e.projectId,
                failureName: identifier,
                date: analysis.date,
              }),
          );

          return extractEventData(eventHit, identifier, column);
        }

        return null;
      } else if (manualFailure) {
        if (column === "systemId") {
          return manualFailure.systemId === null ? "No system" : manualFailure.systemId;
        }

        if (!analysis) return null;

        if (column === "faultStatus") {
          return "NON_MODELLED";
        }

        if (column === "impacts") {
          const value = manualFailureValues[analysis.id];

          if (!value) return null;
          const impacts = Object.entries(value[manualFailure.id]?.impacts ?? {}).flatMap(
            ([quantityId, impact]) => {
              const quantity = quantitiesById.get(quantityId);
              return [
                [`impactValue/${quantity?.name}`, impact],
                [`impactUnit/${quantity?.name}`, quantity?.unit],
              ];
            },
          );

          return impacts;
        }

        if (column === "quantityImpacts") {
          const quantityId = quantitiesByName.get(quantity ?? "")?.id;
          if (!quantityId) return null;

          return [
            [
              `quantityImpacts/${quantity}`,
              manualFailureValues[analysis.id]?.[manualFailure.id]?.impacts[quantityId] ?? null,
            ],
          ];
        }

        if (FaultEventColumns.decode(column)._tag === "Right" && analysis) {
          const relevantEvents = eventsByFault.get(identifier) ?? [];
          const eventHit = relevantEvents.find(
            e =>
              e.type === "MANUAL" &&
              e.startsAt.getTime() <= analysis.date &&
              analysis.date <= getEventEndsAt(e).getTime(),
          );

          return extractEventData(eventHit, identifier, column);
        }

        return null;
      }

      return null;
    };
  };
}

function buildColumnsByIdentifier(
  resources: { kind: string; identifier: string; column: string; quantity?: string }[],
) {
  const columnsByIdentifier = new Map<string, [string, string | undefined][]>();

  for (const { identifier, column, quantity } of inferDefaultColumns(resources)) {
    const columns = columnsByIdentifier.get(identifier);
    if (columns) {
      columns.push([column, quantity]);
    } else {
      columnsByIdentifier.set(identifier, [[column, quantity]]);
    }
  }

  return columnsByIdentifier;
}

function buildDatasetRows(
  columnsByIdentifier: Map<string, [string, string | undefined][]>,
  kind: "QUANTITY" | "FAULT",
  mapFn:
    | ReturnType<ReturnType<typeof mapQuantityResourceToValue>>
    | ReturnType<ReturnType<typeof mapFaultResourceToValue>>,
) {
  return Array.from(columnsByIdentifier, ([identifier, columns]) => {
    const row: Record<string, boolean | string | number | null | undefined> = {
      rowId: getUniqId(),
      kind,
      identifier,
    };

    for (const [column, quantity] of columns) {
      const value = mapFn(identifier, column, quantity);
      if (Array.isArray(value)) {
        for (const [key, entry] of value) {
          if (typeof key === "number" || typeof key === "string") {
            row[key] = entry;
          }
        }
      } else {
        row[column] = value;
      }
    }

    return row;
  });
}

export function mapQuantityResourceToDataset(resources: QuantityResourceT[]) {
  const columnsByIdentifier = buildColumnsByIdentifier(resources);

  return (mapFn: ReturnType<ReturnType<typeof mapQuantityResourceToValue>>) =>
    buildDatasetRows(columnsByIdentifier, "QUANTITY", mapFn);
}

export function mapFaultResourceToDataset(resources: FaultResourceT[]) {
  const columnsByIdentifier = buildColumnsByIdentifier(resources);

  return (mapFn: ReturnType<ReturnType<typeof mapFaultResourceToValue>>) =>
    buildDatasetRows(columnsByIdentifier, "FAULT", mapFn);
}

/**
 * @description
 * Add default columns to our dataset to ensure
 * we have all the required additionnal data to display inside our table or plot.
 */
function inferDefaultColumns(
  resources: { kind: string; identifier: string; column: string; quantity?: string }[],
): { kind: string; identifier: string; column: string; quantity?: string }[] {
  const byIdentifier = resources.reduce((acc, resource) => {
    const existing = acc.get(resource.identifier);
    if (existing) {
      existing.push(resource);
    } else {
      acc.set(resource.identifier, [resource]);
    }
    return acc;
  }, new Map<string, typeof resources>());

  for (const [identifier, resourceGroup] of byIdentifier) {
    const kind = Array.from(new Set(resourceGroup.map(resource => resource.kind)))[0];
    if (!kind) continue;

    const columns = resourceGroup.map(resource => resource.column);
    let hasDate = false;
    let hasQuantityImpacts = false;
    let hasAnalysisColumns = false;
    let hasQuantityValues = false;
    let hasFaultValues = false;

    for (const column of columns) {
      if (column === "date") hasDate = true;
      if (column === "quantityImpacts") hasQuantityImpacts = true;
      if (
        ActiveAnalysisColumns.is(column) ||
        QuantityAnalysisValueColumns.is(column) ||
        QuantityAnalysisSetupComputedColumns.is(column) ||
        FaultConstantColumns.is(column) ||
        FaultValueColumns.is(column) ||
        FaultComputedValueColumns.is(column)
      ) {
        hasAnalysisColumns = true;
      }
      if (
        QuantityAnalysisValueColumns.is(column) ||
        QuantityAnalysisSetupComputedColumns.is(column)
      ) {
        hasQuantityValues = true;
      }
      if (FaultValueColumns.is(column) || FaultComputedValueColumns.is(column)) {
        hasFaultValues = true;
      }
    }

    const defaultColumns = new Set(["id", "unit", "description", "displayName"]);

    if (hasDate) {
      defaultColumns.add("label");
      defaultColumns.add("group");
      defaultColumns.add("validity");
      defaultColumns.add("hour");
      defaultColumns.add("dayOfYear");
    }

    if (hasQuantityImpacts) {
      defaultColumns.add("impacts");
    }

    if (hasAnalysisColumns) {
      defaultColumns.add("date");
      defaultColumns.add("label");
      defaultColumns.add("group");
      defaultColumns.add("hour");
      defaultColumns.add("dayOfYear");
    }

    if (hasQuantityValues) {
      defaultColumns.add("validity");
    }

    if (hasFaultValues) {
      defaultColumns.add("faultStatus");
    }

    const existingColumns = new Set(
      resourceGroup.map(resource => `${resource.column}::${resource.quantity ?? ""}`),
    );

    for (const column of defaultColumns) {
      const key = `${column}::`;
      if (existingColumns.has(key)) continue;
      resourceGroup.push({ kind, identifier, column });
      existingColumns.add(key);
    }
  }

  return Array.from(byIdentifier.values()).flat(2);
}

/** @note don't rely on window.crypto.randomUUID() to gain few ms */
function getUniqId() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
